import { useState } from 'react';
import './insurance-ar.css';
import { getSettingsSync, saveFollowUpSettings, useArVersion } from './store';
import { SectionHeader } from '../settings-d/primitives';
import { NumberSetting, SavedHint, SettingsChangeFooter, recordSettingsChange, useCanEditSettings, useSavedHint } from '../settings-d/settingsEdit';
import { FLAG_DEFAULTS, FLAG_FOOTER_ID, FLAG_LIMITS, setFlagSetting, useFlagSettings } from './flagSettings';

const LIMIT_FOOTER_ID = 'follow-up-limit';

function FlagRow({
  before,
  after,
  helper,
  children,
}: {
  before: string;
  after: string;
  helper: string;
  children: React.ReactNode;
}) {
  return (
    <div className="setd-row" style={{ alignItems: 'flex-start' }}>
      <div style={{ minWidth: 0 }}>
        <div className="setd-row-title" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {before}
          {children}
          {after}
        </div>
        <div className="setd-ed-help">{helper}</div>
      </div>
    </div>
  );
}

/**
 * Settings › Follow-up — direction D. Mounted as a slot in the settings-d
 * shell, so it uses that shell's `setd-card` chrome. The interactive controls
 * (stepper, table, buttons) keep their feature styling from insurance-ar.css.
 */
export function FollowUpSettings() {
  useArVersion();
  const settings = getSettingsSync();
  const canEdit = useCanEditSettings();
  const flags = useFlagSettings();
  const limitHint = useSavedHint();
  const [limit, setLimit] = useState(settings.limit);
  const [moved, setMoved] = useState(0);

  // Changes save as you make them (D221): each tap on the stepper saves.
  const change = (next: number) => {
    const clamped = Math.max(1, Math.min(10, next));
    if (clamped === limit) return;
    setLimit(clamped);
    setMoved(saveFollowUpSettings(clamped));
    recordSettingsChange(LIMIT_FOOTER_ID, `Follow-up limit: ${String(limit)} to ${String(clamped)}`);
    limitHint.flash();
  };

  return (
    <>
      <section className="setd-card" aria-labelledby="iar-lim">
        <SectionHeader n="" title="Follow up on each unpaid claim up to" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            {canEdit ? (
              <div className="iar-step" role="group" aria-label="Number of follow-ups">
                <button type="button" aria-label="One fewer" disabled={limit <= 1} onClick={() => { change(limit - 1); }}>−</button>
                <output aria-live="polite">{limit}</output>
                <button type="button" aria-label="One more" disabled={limit >= 10} onClick={() => { change(limit + 1); }}>+</button>
              </div>
            ) : (
              <span className="mono" style={{ fontSize: 15 }}>{limit}</span>
            )}
            <span style={{ fontSize: 15 }}>times</span>
            <SavedHint show={limitHint.saved} />
          </div>
          <div className="iar-notice" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div><strong style={{ color: 'var(--fg)' }}>One follow-up</strong> is one round: ELVA checks the claim electronically and, if that doesn&rsquo;t settle it, on the payer&rsquo;s portal and by phone. Sending what the payer asks for, or resending a claim the payer lost, is part of the same round.</div>
            <div><strong style={{ color: 'var(--fg)' }}>When:</strong> the first follow-up comes once the payer&rsquo;s usual time has passed, or earlier: when a claim has been unpaid longer than your threshold below, or a state&rsquo;s prompt-pay deadline has passed. Then one every 14 days.</div>
            <div><strong style={{ color: 'var(--fg)' }}>After the last one:</strong> the claim goes to Human in the loop, where someone lets ELVA keep going or stops and writes it off.</div>
          </div>
          {moved > 0 ? (
            <div className="iar-meta" role="status">{`${String(moved)} ${moved === 1 ? 'claim' : 'claims'} at or past the new limit went to Human in the loop.`}</div>
          ) : null}
          <SettingsChangeFooter id={LIMIT_FOOTER_ID} initial="Oct 1" note="Applies to claims already waiting, too." className="iar-meta" />
        </div>
      </section>

      <section className="setd-card" aria-labelledby="iar-flags">
        <SectionHeader n="" title="When ELVA flags it" />
        <p className="setd-desc">ELVA flags a claim for your attention when it passes these. A change applies from today on.</p>
        <FlagRow before="A claim that was made but not sent: after" after="days" helper={`ELVA's default: ${String(FLAG_DEFAULTS.notSentDays)} days`}>
          <NumberSetting
            label="Days before a claim that was made but not sent is flagged"
            value={flags.notSentDays}
            {...FLAG_LIMITS.notSentDays}
            unit="days"
            readOnly={!canEdit}
            onCommit={(n) => { setFlagSetting('notSentDays', n); }}
          />
        </FlagRow>
        <FlagRow before="A claim that hasn't been paid:" after="days after it was first sent" helper={`ELVA's default: ${String(FLAG_DEFAULTS.unpaidDays)} days`}>
          <NumberSetting
            label="Days after first sent before an unpaid claim is flagged"
            value={flags.unpaidDays}
            {...FLAG_LIMITS.unpaidDays}
            unit="days"
            readOnly={!canEdit}
            onCommit={(n) => { setFlagSetting('unpaidDays', n); }}
          />
        </FlagRow>
        <FlagRow before="Differences under" after="aren't chased" helper={`ELVA's default: $${String(FLAG_DEFAULTS.toleranceDollars)}`}>
          <NumberSetting
            label="Dollar amount under which differences aren't chased"
            value={flags.toleranceDollars}
            {...FLAG_LIMITS.toleranceDollars}
            prefix="$"
            readOnly={!canEdit}
            onCommit={(n) => { setFlagSetting('toleranceDollars', n); }}
          />
        </FlagRow>
        <SettingsChangeFooter id={FLAG_FOOTER_ID} />
      </section>

      <section className="setd-card" aria-labelledby="iar-ut">
        <SectionHeader n="" title="How long your payers usually take" />
        <p className="setd-desc">From your own claims over the last 12 months. ELVA uses this to decide when a claim is late.</p>
        <table className="iar-tbl">
          <thead><tr><th scope="col">Payer</th><th scope="col">Usually pays in</th></tr></thead>
          <tbody>
            {settings.payerTimes.map((p) => (
              <tr key={p.payer}><td>{p.payer}</td><td>{p.usually}</td></tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}
